import { Controller } from '@hotwired/stimulus';

import {
  updateDocumentUserState,
  updateProgressFromResponse,
} from '../utils/case_experience_in_rails';
import { postDocumentUserLog } from '../src/react/helpers/DocumentUserLog';
import offlineProgressSync from '../src/react/helpers/OfflineProgressSync';
import {
  paintIllnessScriptProgressOffline,
  recordVisitedScoredUid,
} from '../utils/illness_script_offline_progress';

export default class extends Controller {
  static targets = ['content', 'indicator'];
  static values = {
    autoCollapseSiblings: Boolean, // Enable "only one uncollapsed section" mode
    sectionLevel: Number,
    state: Object, // { collapsed: Boolean, invisible: Boolean }
    aiEnabled: { type: Boolean, default: false },
    aiDisplayFlag: { type: String, default: null },
    vpeEnabled: { type: Boolean, default: false },
    aiFallbackCards: { type: Boolean, default: false },
    documentId: Number,
    userId: Number,
    nodeUid: String,
  };

  connect() {
    // Initialize UI state on connect based on initial collapsed value
    this.updateUI();

    // If this section is marked as progress, log OpenedDocumentLog
    const isLogProgress = this.data.get('log-progress') === 'true';
    if (isLogProgress) {
      // Mark section as collapsed in DUS using the actual state
      const collapsed = this.stateValue?.collapsed === true;
      this.submitDocumentUserState(this.documentIdValue, this.userIdValue, this.nodeUidValue, collapsed);

      // Log OpenedDocumentLog
      this.submitDocumentUserLog(this.documentIdValue, this.nodeUidValue, 'OpenedDocumentLog');
    }

    // Track time when section is expanded
    this.timeStarted = null;
    if (this.stateValue?.collapsed !== true) {
      this.startTimeTracking();
    }
  }

  disconnect() {
    // Stop time tracking when controller is disconnected
    this.stopTimeTracking();
  }

  // This Stimulus lifecycle callback runs whenever the aiFallbackCardsValue changes.
  // It ensures the UI is updated to reflect the current AI fallback card state,
  // such as when AI features are enabled/disabled or the user transitions to fallback mode.
  aiFallbackCardsValueChanged() {
    this.updateUI();
  }

  /** Level from DOM; Stimulus `sectionLevelValue` can be unset even when the data attribute exists. */
  domSectionLevel() {
    const raw = this.element.dataset?.sectionCollapseSectionLevelValue;
    if (raw === undefined || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }

  toggle(event) {
    // Prevent default if clicked on button
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // If already expanded, collapse it and stop tracking time
    if (this.stateValue?.collapsed === false) {
      this.stopTimeTracking();
      this.stateValue = { ...this.stateValue, collapsed: true };
      this.updateUI();
      this.persistState();
      return;
    }

    // If expanding, log OpenedSectionLog
    if (this.stateValue?.collapsed === true) {
      this.submitDocumentUserLog(this.documentIdValue, this.nodeUidValue, 'OpenedSectionLog');
    }

    // If auto-collapse is enabled (student mode with onlyOneUncollapsedSection)
    // collapse all other level 1 sections before expanding this one
    if (this.autoCollapseSiblingsValue && this.domSectionLevel() === 1) {
      this.collapseSiblings();
    }

    // Expand the section
    this.stateValue = { ...this.stateValue, collapsed: false };

    // Start tracking time on this section
    this.startTimeTracking();

    // Update UI - content stays in DOM, just toggle CSS classes
    this.updateUI();
    this.persistState();
  }

  handleKeydown(event) {
    // Support keyboard navigation (Enter or Space to toggle)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle(event);
      // Also apply current-element class to the toggled button
      const btn = event.target;
      if (btn && btn.classList && btn.classList.contains('aet-plus-minus-button')) {
        // Only consider visible level 1 buttons
        const buttons = Array.from(document.querySelectorAll('.aet-plus-minus-button')).filter(
          b => b.getAttribute('data-level') === '1' && b.offsetParent !== null
        );
        buttons.forEach(b => b.classList.remove('current-element'));
        btn.classList.add('current-element');
      }
      return;
    }

    // Support ArrowLeft/ArrowRight to move between collapse/expand buttons
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      // Only include .aet-plus-minus-button with data-level="1" on the button itself
      const buttons = Array.from(document.querySelectorAll('.aet-plus-minus-button')).filter(
        btn => btn.getAttribute('data-level') === '1' && btn.offsetParent !== null
      );
      const idx = buttons.indexOf(document.activeElement);
      if (idx === -1) return;
      event.preventDefault();
      let nextIdx;
      if (event.key === 'ArrowRight') {
        nextIdx = (idx + 1) % buttons.length;
      } else {
        nextIdx = (idx - 1 + buttons.length) % buttons.length;
      }
      setTimeout(() => {
        const nextButton = buttons[nextIdx];
        nextButton.focus();
        buttons.forEach((button, buttonIdx) => {
          if (buttonIdx === nextIdx) {
            button.classList.add('current-element');
          } else {
            button.classList.remove('current-element');
          }
        });
      }, 0);
    }
  }

  startTimeTracking() {
    if (this.timeStarted) return; // Already tracking

    this.timeStarted = Date.now();

    // Mark section as visited
    const nodeUid = this.element.dataset.uid;
    if (nodeUid) {
      this.element.classList.add('visited');

      // Dispatch event for progress tracking
      this.dispatch('sectionVisited', {
        detail: { nodeUid: nodeUid, timestamp: this.timeStarted },
      });
    }
  }

  stopTimeTracking() {
    if (!this.timeStarted) return; // Not tracking

    const timeSpent = Math.floor((Date.now() - this.timeStarted) / 1000); // in seconds
    const nodeUid = this.element.dataset.uid;

    if (nodeUid && timeSpent > 0) {
      // Dispatch event with time spent data
      this.dispatch('sectionTimeTracked', {
        detail: { nodeUid: nodeUid, timeSpent: timeSpent },
      });
    }

    this.timeStarted = null;
  }

  collapseSiblings() {
    // Find all level 1 collapsible sections (siblings)
    const allSections = document.querySelectorAll(
      '[data-section-collapse-section-level-value="1"]',
    );

    allSections.forEach((section) => {
      // Skip this section
      if (section === this.element) return;

      // Get the controller instance for this section
      const controller = this.application.getControllerForElementAndIdentifier(
        section,
        'section-collapse',
      );

      if (controller && controller.stateValue?.collapsed === false) {
        // Stop time tracking on the sibling before collapsing
        controller.stopTimeTracking();

        // Collapse the sibling section
        controller.stateValue = { ...controller.stateValue, collapsed: true };
        controller.updateUI();
        controller.persistState();
      }
    });
  }

  persistState() {
    // Persist collapsed state to server via API
    // This matches the React implementation
    if (typeof this.stateValue === 'object') {
      this.submitDocumentUserState(this.documentIdValue, this.userIdValue, this.nodeUidValue, this.stateValue.collapsed);
    }
  }

  /** Student case view hides collapsed bodies visually; editor modes keep body in the layout. */
  isStudentDisplayMode() {
    const docBody = this.element.closest('.doc-body');
    return !!docBody?.classList.contains('student-display-mode');
  }

  updateSectionBodyAccessibility(collapsed) {
    const content = this.hasContentTarget
      ? this.contentTarget
      : this.element.querySelector('.doc-section-body');
    if (!content) return;

    if (collapsed && this.isStudentDisplayMode()) {
      content.setAttribute('aria-hidden', 'true');
    } else {
      content.removeAttribute('aria-hidden');
    }
  }

  updateUI() {
    const section = this.element;
    // AI on/off pair: clear marker each pass so visible sections do not keep .doc-section--hide-for-ai-pair.
    section.classList.remove('doc-section--hide-for-ai-pair');
    const indicator = this.hasIndicatorTarget
      ? this.indicatorTarget
      : section.querySelector('[data-section-collapse-target="indicator"]');
    const collapsed = Boolean(this.stateValue?.collapsed);

    // For invisible
    // When AI+VPE on, hide aiOff duplicate so only aiOn path shows. Marker opts section out of editor view
    // modes that force display:block on display:none (Doc.scss ungated/full/outline).
    const hideForAiOffPair =
      this.vpeEnabledValue &&
      this.aiEnabledValue &&
      this.aiDisplayFlagValue === 'aiOff' &&
      !this.aiFallbackCardsValue;

    // When AI fallback cards are enabled, and this section is for the "aiOn" display variant,
    // hide it so that only the traditional (non-AI) path is shown—this matches React DocBlockNodes logic.
    const hideForAiOnFallback =
      this.aiFallbackCardsValue === true && this.aiDisplayFlagValue === 'aiOn';

    const visualExpanded = !collapsed;

    section.classList.toggle('collapsed', collapsed);
    section.classList.toggle('expanded', !collapsed);

    this.updateSectionBodyAccessibility(collapsed);

    if (indicator) {
      const button = indicator.querySelector('button');
      if (button) {
        button.className = visualExpanded
          ? 'icon-close-box aet-plus-minus-button'
          : 'icon-open-box aet-plus-minus-button';
        button.setAttribute('aria-label', visualExpanded ? 'Collapse section' : 'Expand section');
        button.setAttribute('aria-expanded', visualExpanded ? 'true' : 'false');
      }
    }

    const topperIndicator = section.querySelector(
      ':scope > .doc-section-header .teaching-point-topper-indicator',
    );
    if (topperIndicator) {
      const expandImg = topperIndicator.querySelector('.teaching-point-topper-circle-expand');
      const collapseImg = topperIndicator.querySelector('.teaching-point-topper-circle-collapse');
      if (expandImg && collapseImg) {
        expandImg.style.display = visualExpanded ? 'none' : 'block';
        collapseImg.style.display = visualExpanded ? 'block' : 'none';
      }
    }

    // Determine if this section should be hidden for AI "on" fallback mode or for the AI "off" VPE pair logic.
    if (hideForAiOnFallback || hideForAiOffPair) {
      // Hide this entire section and add a marker class, so it is excluded from layouts and ARIA navigation.
      section.style.display = 'none';
      section.classList.add('doc-section--hide-for-ai-pair');
    } 
    // Otherwise, check if the section's state specifically requests it to be invisible.
    else if (this.stateValue?.invisible === true) {
      // Hide the section, but do not add the AI pair marker as this is a general invisible case.
      section.style.display = 'none';
    } 
    // In all other cases, ensure the section is visible and any custom display is reset.
    else {
      section.style.display = '';
    }
  }

  async submitDocumentUserState(documentId, userId, nodeUid, value) {
    if (!documentId || !userId || !nodeUid) return;

    try {
      const response = await updateDocumentUserState({
        documentId: documentId,
        userId: userId,
        nodeUid: nodeUid,
        key: 'collapsed',
        value: value,
      });
      updateProgressFromResponse(response);
    } catch (error) {
      console.error('Error updating document user state:', error);
    }
  }

  submitDocumentUserLog(documentId, nodeUid, type) {
    if (!documentId || !nodeUid || !type) return;
    // postDocumentUserLog expects (document_id, log_type, args)
    // node_uid is passed as part of args
    postDocumentUserLog(documentId, type, { node_uid: nodeUid });
    if (type === 'OpenedSectionLog') {
      recordVisitedScoredUid(nodeUid);
      if (offlineProgressSync.shouldQueue()) {
        paintIllnessScriptProgressOffline({ documentId, extraUid: nodeUid });
      }
    }
  }
}
